import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import { toast } from "sonner";
import { VoipLoginModal } from "./VoipLoginModal";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { appendDealHistoryDirect } from "../deals/useDealHistory";

interface VoipStatus {
  authenticated: boolean;
  email?: string;
  ramal?: string;
}

interface VoipContextType {
  isConnected: boolean;
  userEmail?: string;
  ramal?: string;
  isLoading: boolean;
  isCalling: boolean;
  isLoginModalOpen: boolean;
  openLoginModal: () => void;
  closeLoginModal: () => void;
  checkStatus: () => Promise<boolean>;
  login: (
    email: string,
    pass: string,
  ) => Promise<{ ok: boolean; message?: string }>;
  logout: () => Promise<void>;
  makeCall: (params: {
    phone: string;
    contactName: string;
    dealId?: string | number;
    taskId?: string | number;
  }) => Promise<{ ok: boolean; message: string; callId?: string }>;
}

const VoipContext = createContext<VoipContextType | undefined>(undefined);

export const VoipProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [userEmail, setUserEmail] = useState<string | undefined>(undefined);
  const [ramal, setRamal] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isCalling, setIsCalling] = useState<boolean>(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState<boolean>(false);

  // 1. Checa status da sessão VoIP com cookie httpOnly
  const checkStatus = useCallback(async (): Promise<boolean> => {
    try {
      const res = await fetch("/api/voip", {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "include", // Garante envio dos cookies
      });

      if (res.ok) {
        const data: VoipStatus = await res.json();
        setIsConnected(Boolean(data.authenticated));
        setUserEmail(data.email);
        setRamal(data.ramal);
        return Boolean(data.authenticated);
      }
      setIsConnected(false);
      return false;
    } catch {
      setIsConnected(false);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  // 2. Realiza Login no VoIP (Passos 1 a 3 no backend)
  const login = async (
    email: string,
    pass: string,
  ): Promise<{ ok: boolean; message?: string }> => {
    setIsLoading(true);
    try {
      const res = await fetch("/api/voip?action=login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password: pass }),
      });

      const data = await res.json().catch(() => ({}));

      if (res.ok && data.ok) {
        setIsConnected(true);
        setUserEmail(email);
        setRamal(data.ramal);
        setIsLoginModalOpen(false);
        toast.success(`VoIP conectado com sucesso! Ramal: ${data.ramal}`);
        return { ok: true, message: data.message };
      }

      setIsConnected(false);
      return {
        ok: false,
        message: data.message || "E-mail ou senha inválidos",
      };
    } catch {
      return { ok: false, message: "Erro de conexão, tente novamente" };
    } finally {
      setIsLoading(false);
    }
  };

  // 3. Logout do VoIP
  const logout = async () => {
    try {
      await fetch("/api/voip?action=logout", {
        method: "POST",
        credentials: "include",
      });
    } catch {
      // Ignora erro
    } finally {
      setIsConnected(false);
      setUserEmail(undefined);
      setRamal(undefined);
      toast.info("Desconectado do VoIP");
    }
  };

  // 4. Dispara a chamada (Click-to-Call)
  const makeCall = async (params: {
    phone: string;
    contactName: string;
    dealId?: string | number;
    taskId?: string | number;
  }): Promise<{ ok: boolean; message: string; callId?: string }> => {
    if (isCalling) {
      return { ok: false, message: "Uma chamada já está sendo iniciada..." };
    }

    setIsCalling(true);
    try {
      const res = await fetch("/api/voip?action=call", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(params),
      });

      const data = await res.json().catch(() => ({}));

      if (res.status === 401 && data.needLogin) {
        setIsConnected(false);
        setIsLoginModalOpen(true);
        toast.error("Sua sessão VoIP expirou. Faça login novamente.");
        return { ok: false, message: "Login necessário" };
      }

      if (res.ok && data.ok) {
        const msg = data.message || `Ligando para ${params.contactName}...`;
        toast.success(msg, {
          description: `Seu ramal ${ramal || ""} tocará para conectar a chamada.`,
          duration: 6000,
        });

        const returnedCallId = data.id || data.data?.id;
        const effectiveDealId =
          data.deal_id || data.data?.deal_id || params.dealId;
        const returnedTaskId =
          data.task_id || data.data?.task_id || params.taskId;

        // Rede de segurança no cliente: se o backend não retornou task_id mas temos dealId,
        // cria a atividade (tasks) diretamente e registra no histórico do negócio
        if (effectiveDealId) {
          try {
            appendDealHistoryDirect(effectiveDealId, {
              type: "activity_created",
              label: `Ligação VoIP para ${params.contactName || params.phone}`,
              description: `Chamada VoIP iniciada para ${params.phone}`,
              notes: ramal ? `Disparada via ramal ${ramal}` : undefined,
            });
          } catch {
            // ignora falha no histórico local
          }

          if (!returnedTaskId) {
            try {
              const supabase = getSupabaseClient();
              const { data: dealRow } = await supabase
                .from("deals")
                .select("id, crm_id, company_id, contact_ids, sales_id, name")
                .eq("id", effectiveDealId)
                .maybeSingle();

              if (dealRow) {
                const nowIso = new Date().toISOString();
                const displayTarget =
                  params.contactName || dealRow.name || params.phone || "lead";
                await supabase.from("tasks").insert({
                  crm_id: dealRow.crm_id,
                  deal_id: dealRow.id,
                  company_id: dealRow.company_id || null,
                  contact_id:
                    Array.isArray(dealRow.contact_ids) &&
                    dealRow.contact_ids.length > 0
                      ? dealRow.contact_ids[0]
                      : null,
                  sales_id: dealRow.sales_id || null,
                  type: "chamada",
                  prioridade: "media",
                  text: `Ligação para ${displayTarget}`,
                  notes: `📞 Ligação VoIP realizada para ${displayTarget} (${params.phone})${
                    ramal ? ` via ramal ${ramal}` : ""
                  }.`,
                  data_inicio: nowIso,
                  data_fim: nowIso,
                  due_date: nowIso,
                  done_date: nowIso,
                  custom_fields: {
                    call_id: returnedCallId || null,
                    phone: params.phone,
                    ramal: ramal || null,
                    source: "voip_click_to_call",
                  },
                });
              }
            } catch {
              // ignora falha de fallback
            }
          }
        }

        // Dispara evento global para atualizar imediatamente as atividades e os cards do Kanban
        window.dispatchEvent(
          new CustomEvent("crm:voip_call_started", {
            detail: { dealId: effectiveDealId, callId: returnedCallId },
          }),
        );

        return { ok: true, message: msg, callId: returnedCallId };
      }

      const errMsg = data.message || "Falha ao iniciar a chamada.";
      toast.error(errMsg);
      return { ok: false, message: errMsg };
    } catch {
      const errMsg = "Erro de conexão ao solicitar ligação.";
      toast.error(errMsg);
      return { ok: false, message: errMsg };
    } finally {
      setIsCalling(false);
    }
  };

  return (
    <VoipContext.Provider
      value={{
        isConnected,
        userEmail,
        ramal,
        isLoading,
        isCalling,
        isLoginModalOpen,
        openLoginModal: () => setIsLoginModalOpen(true),
        closeLoginModal: () => setIsLoginModalOpen(false),
        checkStatus,
        login,
        logout,
        makeCall,
      }}
    >
      {children}
      <VoipLoginModal />
    </VoipContext.Provider>
  );
};

export const useVoip = () => {
  const context = useContext(VoipContext);
  if (!context) {
    throw new Error("useVoip deve ser utilizado dentro de um VoipProvider");
  }
  return context;
};
